import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PageSEO from '@/components/feature/PageSEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articlesData } from '@/mocks/articleData';

const defaultArticle = articlesData['fifa-partnership'];

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = articlesData[slug || ''] || defaultArticle;

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: '新闻资讯', path: '/category/news' },
    { label: article.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: article.title,
        description: article.seoDescription,
        author: {
          '@type': 'Organization',
          name: article.author,
        },
        datePublished: article.date,
        dateModified: article.date,
        publisher: {
          '@type': 'Organization',
          name: 'NOPAY钱包',
          url: 'http://dajiang03.cn/',
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `http://dajiang03.cn/article/${slug}`,
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbItems.map((item, idx) => ({
          '@type': 'ListItem',
          position: idx + 1,
          name: item.label,
          ...(item.path ? { item: `http://dajiang03.cn/${item.path}` } : {}),
        })),
      },
    ],
  };

  const relatedArticles = Object.entries(articlesData)
    .filter(([key]) => key !== slug)
    .slice(0, 3)
    .map(([key, val]) => ({ slug: key, ...val }));

  return (
    <>
      <PageSEO
        title={article.title}
        description={article.seoDescription}
        keywords={`NOPAY钱包,${article.tags.join(',')},2026世界杯`}
        canonicalUrl={`http://dajiang03.cn/article/${slug}`}
        ogType="article"
        schemaJson={articleSchema}
      />

      <Navbar />

      <main>
        <section className="relative w-full h-[45vh] min-h-[300px] flex items-center justify-center overflow-hidden">
          <img
            src={article.heroImage}
            alt={article.title}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/55"></div>
          <div className="relative z-10 w-full max-w-3xl mx-auto px-4 md:px-6 text-center">
            <p className="text-accent-400 text-xs tracking-wide font-label mb-3">
              {article.category}
            </p>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-white mb-3 leading-snug">
              {article.title}
            </h1>
            <p className="text-white/70 text-sm max-w-xl mx-auto">
              {article.subtitle}
            </p>
          </div>
        </section>

        <article className="py-12 md:py-16 bg-background-50">
          <div className="mx-auto px-4 md:px-6 max-w-3xl">
            <div className="mb-6">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            <div className="flex flex-wrap items-center gap-4 mb-8 pb-6 border-b border-background-200">
              <span className="text-sm text-foreground-500">
                <i className="ri-user-line mr-1"></i>{article.author}
              </span>
              <span className="text-sm text-foreground-500">
                <i className="ri-calendar-line mr-1"></i>{article.date}
              </span>
              <span className="text-sm text-foreground-500">
                <i className="ri-folder-line mr-1"></i>{article.category}
              </span>
            </div>

            <div className="prose-custom">
              {article.content.map((block, index) => {
                if (block.type === 'heading') {
                  const Tag = `h${block.level || 2}` as keyof JSX.IntrinsicElements;
                  return (
                    <Tag key={index} className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">
                      {block.text}
                    </Tag>
                  );
                }
                if (block.type === 'quote') {
                  return (
                    <blockquote key={index} className="border-l-4 border-accent-500 pl-5 py-2 my-6 text-foreground-700 italic text-base leading-relaxed">
                      {block.text}
                    </blockquote>
                  );
                }
                return (
                  <p key={index} className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">
                    {block.text}
                  </p>
                );
              })}
            </div>

            <div className="flex flex-wrap gap-2 mt-10 pt-6 border-t border-background-200">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-12 md:py-16 bg-background-100">
            <div className="mx-auto px-4 md:px-6 max-w-6xl">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-8">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                {relatedArticles.map((item) => (
                  <Link
                    key={item.slug}
                    to={`/article/${item.slug}`}
                    className="bg-background-50 rounded-lg overflow-hidden group cursor-pointer"
                  >
                    <div className="h-44 overflow-hidden">
                      <img
                        src={item.heroImage}
                        alt={item.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4 md:p-5">
                      <span className="inline-block px-2.5 py-0.5 bg-accent-100 text-accent-700 text-xs font-medium rounded-full mb-2 whitespace-nowrap">
                        {item.category}
                      </span>
                      <h3 className="font-heading text-sm font-bold text-foreground-900 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
                        {item.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}